import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress
} from "@material-ui/core";
import {
  GetApp,
  FileCopy,
  Extension,
  ErrorOutline
} from "@material-ui/icons";
import { toast } from "react-toastify";

const CHROME_EXT_URL = "chrome://extensions";

const useStyles = makeStyles(theme => ({
  paper: { maxWidth: 560 },
  header: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    fontSize: "0.9rem"
  },
  versionBanner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    background: theme.palette.warning.main,
    color: theme.palette.getContrastText(theme.palette.warning.main),
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(2),
    fontSize: "0.85rem"
  },
  downloadBtn: { marginBottom: theme.spacing(2.5) },
  step: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.75),
    alignItems: "flex-start"
  },
  stepNum: {
    flex: "0 0 auto",
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8rem",
    fontWeight: 700
  },
  stepText: { fontSize: "0.9rem", lineHeight: 1.55 },
  code: {
    fontFamily: "monospace",
    fontSize: "0.82rem",
    background: theme.palette.grey[100],
    borderRadius: 4,
    padding: "1px 6px"
  },
  urlRow: {
    display: "inline-flex",
    alignItems: "center",
    background: theme.palette.grey[100],
    borderRadius: 4,
    padding: "0 2px 0 8px",
    verticalAlign: "middle"
  },
  warn: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.error.main,
    fontSize: "0.85rem",
    marginTop: theme.spacing(1.5)
  }
}));

const ConectorExtensionModal = ({
  open,
  mode = "install",
  installedVersion,
  currentVersion,
  downloadUrl,
  onClose,
  onContinue
}) => {
  const classes = useStyles();
  const [checking, setChecking] = useState(false);
  const [failed, setFailed] = useState(false);
  const isUpdate = mode === "update";

  useEffect(() => {
    if (open) {
      setFailed(false);
      setChecking(false);
    }
  }, [open]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(CHROME_EXT_URL);
      toast.success("Endereço copiado — cole na barra do Chrome.");
    } catch (_) {
      toast.info(`Copie manualmente: ${CHROME_EXT_URL}`);
    }
  };

  const handleContinue = async () => {
    setFailed(false);
    setChecking(true);
    try {
      const ok = await onContinue();
      if (!ok) setFailed(true);
    } finally {
      setChecking(false);
    }
  };

  const ChromeUrl = (
    <span className={classes.urlRow}>
      <span className={classes.code} style={{ background: "transparent", padding: 0 }}>
        {CHROME_EXT_URL}
      </span>
      <IconButton size="small" onClick={copyUrl} title="Copiar endereço">
        <FileCopy fontSize="inherit" />
      </IconButton>
    </span>
  );

  const installSteps = [
    <>Baixe o pacote acima e <b>descompacte</b> em uma pasta fixa (não apague depois).</>,
    <>Abra {ChromeUrl} no Google Chrome.</>,
    <>Ative o <b>Modo de Desenvolvedor</b> no canto superior direito.</>,
    <>Clique em <b>“Carregar sem compactação”</b> e selecione a pasta que contém o arquivo <span className={classes.code}>manifest.json</span>.</>,
    <>Volte aqui e clique em <b>“Já instalei — continuar”</b>.</>
  ];

  const updateSteps = [
    <>Baixe o pacote novo acima e <b>descompacte por cima</b> da pasta atual (substituindo os arquivos).</>,
    <>Abra {ChromeUrl} e clique no ícone de <b>recarregar (↻)</b> do “Conector WhatsApp”.</>,
    <>Volte aqui e clique em <b>“Já recarreguei — continuar”</b>.</>
  ];

  const steps = isUpdate ? updateSteps : installSteps;

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <DialogTitle disableTypography>
        <Box className={classes.header}>
          <Extension color="primary" />
          <Typography variant="h6">
            {isUpdate ? "Atualize a extensão Conector" : "Instale a extensão Conector"}
          </Typography>
        </Box>
        <Typography className={classes.subtitle}>
          {isUpdate
            ? "Sua extensão está desatualizada. Atualize para continuar conectando via WhatsApp Web."
            : "Para conectar via WhatsApp Web, instale a extensão do Chrome seguindo os passos abaixo."}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {isUpdate && (
          <Box className={classes.versionBanner}>
            <ErrorOutline fontSize="small" />
            <span>
              Instalada: <b>{installedVersion || "?"}</b> &nbsp;·&nbsp; Atual: <b>{currentVersion || "?"}</b>
            </span>
          </Box>
        )}

        <Button
          className={classes.downloadBtn}
          variant="contained"
          color="primary"
          startIcon={<GetApp />}
          href={downloadUrl}
          component="a"
          fullWidth
        >
          {isUpdate ? "Baixar versão nova (.zip)" : "Baixar extensão (.zip)"}
        </Button>

        {steps.map((content, i) => (
          <Box className={classes.step} key={i}>
            <span className={classes.stepNum}>{i + 1}</span>
            <span className={classes.stepText}>{content}</span>
          </Box>
        ))}

        {failed && (
          <Box className={classes.warn}>
            <ErrorOutline fontSize="small" />
            <span>
              Ainda não detectamos a extensão. Confira os passos (Modo de Desenvolvedor ativo,
              pasta correta) e tente de novo.
            </span>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={checking}>
          Cancelar
        </Button>
        <Button
          onClick={handleContinue}
          color="primary"
          variant="contained"
          disabled={checking}
          startIcon={checking ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isUpdate ? "Já recarreguei — continuar" : "Já instalei — continuar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConectorExtensionModal;
